import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Wallet, Coins, Smartphone, Send, LogOut, 
  Eye, ArrowUpRight, Search, CheckCircle, Shield, CreditCard, MapPin, Zap, UserCheck, AlertCircle, Receipt
} from 'lucide-react';
import { QRWalletCode } from '@/components/QRWallet';
import MerchantFinder from '@/components/MerchantFinder';
import NfcTransfer from '@/components/NfcTransfer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDialog, setActiveDialog] = useState(null);

  const token = localStorage.getItem('mojo_token');
  const user = JSON.parse(localStorage.getItem('mojo_user') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchWalletData();
  }, [token, navigate]);

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallet(response.data);
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#FAF5F0] to-[#F5E8D8]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F2B10C] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-[#1B0C34]">Loading...</p>
        </div>
      </div>
    );
  }

  const quickActions = [
    { 
      id: 'nfc',
      icon: <Zap className="w-8 h-8" />, 
      title: 'NFC Transfer', 
      description: 'FREE instant tap-to-pay',
      color: 'from-[#F2B10C] to-[#d99a0a]'
    },
    { 
      id: 'topup',
      icon: <CreditCard className="w-8 h-8" />, 
      title: 'Add Funds', 
      description: 'Top-up wallet from card',
      color: 'from-[#145539] to-[#1e7049]'
    },
    { 
      id: 'balance',
      icon: <Search className="w-8 h-8" />, 
      title: 'Account Enquiry', 
      description: 'Search Postbank account',
      color: 'from-[#1B0C34] to-[#2D1854]'
    },
    { 
      id: 'token',
      icon: <CreditCard className="w-8 h-8" />, 
      title: 'Payment Token', 
      description: 'Generate payment token',
      color: 'from-[#F2B10C] to-[#d99a0a]'
    },
    { 
      id: 'send',
      icon: <Send className="w-8 h-8" />, 
      title: 'Send Money', 
      description: 'Transfer to phone number',
      color: 'from-[#145539] to-[#1e7049]'
    },
    { 
      id: 'ace',
      icon: <Coins className="w-8 h-8" />, 
      title: 'Buy ACE Token', 
      description: 'Purchase stable coin',
      color: 'from-purple-600 to-purple-800'
    },
    { 
      id: 'airtime',
      icon: <Smartphone className="w-8 h-8" />, 
      title: 'Airtime & Data', 
      description: 'Buy airtime or data',
      color: 'from-blue-600 to-blue-800'
    },
    { 
      id: 'merchants',
      icon: <MapPin className="w-8 h-8" />, 
      title: 'Find Merchants', 
      description: 'Locate nearby Mojo Money',
      color: 'from-orange-600 to-orange-800'
    },
    { 
      id: 'profile',
      icon: <UserCheck className="w-8 h-8" />, 
      title: 'Profile & Settings', 
      description: 'Edit your details',
      color: 'from-green-600 to-green-800'
    },
    { 
      id: 'transactions',
      icon: <Receipt className="w-8 h-8" />, 
      title: 'Recent Transactions', 
      description: 'View transaction history',
      color: 'from-indigo-600 to-indigo-800'
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]" data-testid="customer-dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/sticker.svg" alt="Mojo Money" className="h-10 w-10" />
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Mojo Money</h1>
              <p className="text-xs text-[#145539]">Customer App</p>
            </div>
          </div>
          <Button
            data-testid="customer-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            size="sm"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Verification Status */}
        {wallet?.verification_status && (
          <Card className={`mb-4 border-2 ${wallet.verification_status.verified ? 'border-[#145539] bg-[#145539]/5' : 'border-orange-500 bg-orange-50'}`} data-testid="customer-verification-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                {wallet.verification_status.verified ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-[#145539]" />
                    <div className="flex-1">
                      <p className="font-semibold text-[#1B0C34] text-sm">ID Verified</p>
                      {wallet.verification_status.score && (
                        <p className="text-xs text-[#1B0C34]/70">Score: {wallet.verification_status.score}%</p>
                      )}
                    </div>
                    <span className="px-2 py-1 bg-[#145539] text-white text-xs font-semibold rounded-full">
                      ✓ VERIFIED
                    </span>
                  </>
                ) : (
                  <>
                    <Shield className="w-6 h-6 text-orange-500" />
                    <div className="flex-1">
                      <p className="font-semibold text-orange-700 text-sm">ID Verification Required</p>
                      <p className="text-xs text-orange-600">Complete verification to unlock all transactions</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate(`/verify?id_number=${user.id_number || ''}&user_type=customer`);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="customer-verify-btn"
                    >
                      <Shield className="mr-1 w-3 h-3" />
                      Verify Now
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wallet Balance Card */}
        <Card className="mb-6 bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white" data-testid="customer-wallet-card">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardDescription className="text-white/70">Welcome back,</CardDescription>
                <CardTitle className="text-2xl">{user.full_name}</CardTitle>
              </div>
              {wallet?.wallet_id && (
                <QRWalletCode 
                  walletId={wallet.wallet_id} 
                  userName={user.full_name}
                  userType="customer"
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-white/70 mb-1">Wallet Balance</p>
                <p className="text-2xl font-bold" data-testid="customer-wallet-balance">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <p className="text-sm text-white/70 mb-1">ACE Tokens</p>
                <p className="text-2xl font-bold" data-testid="customer-ace-balance">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-white/10 rounded-lg">
              <p className="text-xs text-white/70">Wallet ID</p>
              <p className="text-sm font-mono" data-testid="customer-wallet-id">{wallet?.wallet_id}</p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          {quickActions.map((action, index) => (
            <Card
              key={index}
              className={`card-hover cursor-pointer bg-gradient-to-br ${action.color} text-white`}
              onClick={() => setActiveDialog(action.id)}
              data-testid={`action-${action.id}`}
            >
              <CardHeader className="pb-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                  {action.icon}
                </div>
                <CardTitle className="text-lg">{action.title}</CardTitle>
                <CardDescription className="text-white/70 text-xs">{action.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </main>

      {/* Dialogs */}
      <Dialog open={activeDialog === 'nfc'} onOpenChange={(open) => !open && setActiveDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>NFC Wallet Transfer</DialogTitle>
            <DialogDescription>
              Send or receive money instantly via NFC or QR code - FREE transfers!
            </DialogDescription>
          </DialogHeader>
          <NfcTransfer userType="customer" />
        </DialogContent>
      </Dialog>

      <WalletTopUpDialog open={activeDialog === 'topup'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <BalanceEnquiryDialog open={activeDialog === 'balance'} onClose={() => setActiveDialog(null)} />
      <PaymentTokenDialog open={activeDialog === 'token'} onClose={() => setActiveDialog(null)} wallet={wallet} />
      <SendMoneyDialog open={activeDialog === 'send'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <BuyACEDialog open={activeDialog === 'ace'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} wallet={wallet} />
      <AirtimeDataDialog open={activeDialog === 'airtime'} onClose={() => { setActiveDialog(null); fetchWalletData(); }} />
      <ProfileDialog 
        open={activeDialog === 'profile'} 
        onClose={() => setActiveDialog(null)}
        user={user}
        onUpdate={fetchWalletData}
      />
      <TransactionsDialog 
        open={activeDialog === 'transactions'} 
        onClose={() => setActiveDialog(null)}
        wallet={wallet}
      />
      <MerchantFinder open={activeDialog === 'merchants'} onClose={() => setActiveDialog(null)} />
    </div>
  );
};

// Wallet Top-Up Dialog
const WalletTopUpDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    amount: '',
    card_type: 'debit',
    card_number: '',
    card_holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const calculateFee = () => {
    if (!formData.amount) return { fee: 0, net: 0 };
    const amount = parseFloat(formData.amount);
    const feeRate = formData.card_type === 'debit' ? 0.02 : 0.035;
    const fee = amount * feeRate;
    const net = amount - fee;
    return { fee, net };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const last4 = formData.card_number.replace(/\s/g, '').slice(-4);
      
      const response = await axios.post(
        `${API}/wallet/topup`,
        {
          amount: parseFloat(formData.amount),
          card_type: formData.card_type,
          card_number_last4: last4,
          card_holder_name: formData.card_holder_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Success! R${response.data.net_credited} added to wallet`);
      setFormData({
        amount: '',
        card_type: 'debit',
        card_number: '',
        card_holder_name: '',
        expiry_month: '',
        expiry_year: '',
        cvv: ''
      });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  const { fee, net } = calculateFee();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Add Funds to Wallet</DialogTitle>
          <DialogDescription>Top-up your wallet using debit or credit card</DialogDescription>
        </DialogHeader>

        <div className="p-3 bg-[#F2B10C]/10 border border-[#F2B10C]/30 rounded-lg mb-2">
          <p className="text-xs text-[#1B0C34]/70">
            • Debit: 2% fee • Credit: 3.5% fee<br/>
            • Fee deducted from top-up amount
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="customer_topup_amount">Amount (ZAR)</Label>
            <Input
              id="customer_topup_amount"
              type="number"
              step="0.01"
              min="10"
              data-testid="customer-topup-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && (
              <div className="mt-2 p-2 bg-[#FAF5F0] rounded text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Amount:</span>
                  <span className="font-semibold">R{parseFloat(formData.amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fee ({formData.card_type === 'debit' ? '2%' : '3.5%'}):</span>
                  <span className="text-red-600">-R{fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-[#145539] pt-1 border-t">
                  <span>You receive:</span>
                  <span>R{net.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Card Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="customer-topup-debit-btn"
                variant={formData.card_type === 'debit' ? 'default' : 'outline'}
                className={formData.card_type === 'debit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1 text-sm' : 'flex-1 text-sm'}
                onClick={() => setFormData({ ...formData, card_type: 'debit' })}
              >
                Debit
              </Button>
              <Button
                type="button"
                data-testid="customer-topup-credit-btn"
                variant={formData.card_type === 'credit' ? 'default' : 'outline'}
                className={formData.card_type === 'credit' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1 text-sm' : 'flex-1 text-sm'}
                onClick={() => setFormData({ ...formData, card_type: 'credit' })}
              >
                Credit
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="customer_card_holder">Card Holder</Label>
            <Input
              id="customer_card_holder"
              data-testid="customer-card-holder-input"
              value={formData.card_holder_name}
              onChange={(e) => setFormData({ ...formData, card_holder_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="customer_card_number">Card Number</Label>
            <Input
              id="customer_card_number"
              data-testid="customer-card-number-input"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              value={formData.card_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                setFormData({ ...formData, card_number: formatted });
              }}
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label htmlFor="customer_exp_month" className="text-xs">Month</Label>
              <Input
                id="customer_exp_month"
                data-testid="customer-exp-month-input"
                placeholder="MM"
                maxLength="2"
                value={formData.expiry_month}
                onChange={(e) => setFormData({ ...formData, expiry_month: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer_exp_year" className="text-xs">Year</Label>
              <Input
                id="customer_exp_year"
                data-testid="customer-exp-year-input"
                placeholder="YY"
                maxLength="2"
                value={formData.expiry_year}
                onChange={(e) => setFormData({ ...formData, expiry_year: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer_cvv" className="text-xs">CVV</Label>
              <Input
                id="customer_cvv"
                type="password"
                data-testid="customer-cvv-input"
                placeholder="123"
                maxLength="4"
                value={formData.cvv}
                onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="customer-submit-topup-btn"
            className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Add Funds'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Balance Enquiry Dialog (Postbank REST API - JSON-RPC 2.0)
const BalanceEnquiryDialog = ({ open, onClose }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleEnquiry = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/postbank/customer-search`,
        { 
          phone_number: mobileNumber,
          national_id: idNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.customer) {
        setCustomer(response.data.customer);
        toast.success('Customer information retrieved');
      } else {
        toast.error(response.data.error || 'Customer not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retrieve customer information');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setMobileNumber('');
    setIdNumber('');
    setCustomer(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="balance-enquiry-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Postbank Account Enquiry</DialogTitle>
          <DialogDescription>Search your Postbank account using mobile number and ID (JSON-RPC 2.0)</DialogDescription>
        </DialogHeader>

        {!customer ? (
          <form onSubmit={handleEnquiry} className="space-y-4">
            <div>
              <Label htmlFor="mobile_num">Mobile Number</Label>
              <Input
                id="mobile_num"
                data-testid="balance-mobile-input"
                placeholder="+27769584862"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">Format: +27XXXXXXXXX</p>
            </div>
            <div>
              <Label htmlFor="id_num">National ID Number</Label>
              <Input
                id="id_num"
                data-testid="balance-id-input"
                placeholder="7503085486088"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ''))}
                maxLength={13}
                required
              />
              <p className="text-xs text-[#1B0C34]/60 mt-1">13-digit SA ID</p>
            </div>
            <Button
              type="submit"
              data-testid="check-balance-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search Account'}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg" data-testid="balance-result">
              <p className="text-sm opacity-70 mb-2">Customer Information</p>
              <p className="text-2xl font-bold mb-3">{customer.firstName} {customer.lastName}</p>
              
              <div className="space-y-1 text-sm border-t border-white/20 pt-3">
                <div className="flex justify-between">
                  <span className="opacity-70">Phone:</span>
                  <span className="font-semibold">{customer.phoneNumber}</span>
                </div>
                
                {customer.accounts && customer.accounts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-xs opacity-70 mb-2">Linked Accounts:</p>
                    {customer.accounts.map((account, idx) => (
                      <div key={idx} className="flex justify-between py-1">
                        <span className="text-xs">{account.accountName}</span>
                        <span className="text-xs font-mono">{account.accountNumber}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleReset}
              data-testid="new-search-btn"
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            >
              New Search
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Payment Token Dialog
const PaymentTokenDialog = ({ open, onClose, wallet }) => {
  const [formData, setFormData] = useState({ 
    amount: '', 
    token_source: 'wallet' 
  });
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const authToken = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) {
      fetchACEInfo();
    }
  }, [open]);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateACE = () => {
    if (!formData.amount || !aceInfo) return 0;
    return (parseFloat(formData.amount) / aceInfo.current_value_zar).toFixed(4);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/wallet/generate-payment-token`,
        { 
          amount: parseFloat(formData.amount),
          token_source: formData.token_source
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setToken(response.data);
      toast.success('Payment token generated from ' + formData.token_source);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setToken(null);
    setFormData({ amount: '', token_source: 'wallet' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="payment-token-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Generate Payment Token</DialogTitle>
          <DialogDescription>Create a payment token from your wallet or ACE tokens</DialogDescription>
        </DialogHeader>

        {/* Balance Display */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-[#FAF5F0] rounded-lg">
          <div>
            <p className="text-xs text-[#1B0C34]/60">Wallet Balance</p>
            <p className="text-sm font-bold text-[#1B0C34]">R{wallet?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div>
            <p className="text-xs text-[#1B0C34]/60">ACE Balance</p>
            <p className="text-sm font-bold text-[#F2B10C]">{wallet?.ace_token_balance?.toFixed(4) || '0.0000'}</p>
          </div>
        </div>

        <form onSubmit={handleGenerate} className="space-y-4">
          {/* Token Source */}
          <div>
            <Label>Token Source</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="select-wallet-token-btn"
                variant={formData.token_source === 'wallet' ? 'default' : 'outline'}
                className={formData.token_source === 'wallet' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, token_source: 'wallet' })}
              >
                Wallet (ZAR)
              </Button>
              <Button
                type="button"
                data-testid="select-ace-token-btn"
                variant={formData.token_source === 'ace_token' ? 'default' : 'outline'}
                className={formData.token_source === 'ace_token' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, token_source: 'ace_token' })}
              >
                ACE Token
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="token_amount">Amount (ZAR)</Label>
            <Input
              id="token_amount"
              type="number"
              step="0.01"
              data-testid="token-amount-input"
              placeholder="Enter amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
            {formData.amount && formData.token_source === 'ace_token' && aceInfo && (
              <p className="text-xs text-[#145539] font-medium mt-1">
                Requires: {calculateACE()} ACE tokens (@ R{aceInfo.current_value_zar}/token)
              </p>
            )}
          </div>

          <Button
            type="submit"
            data-testid="generate-token-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Generating...' : 'Generate Payment Token'}
          </Button>
        </form>

        {token && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="payment-token-result">
            <p className="text-sm opacity-70 mb-1">Payment Token</p>
            <p className="text-2xl font-bold font-mono mb-3">{token.payment_token}</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="opacity-70">Amount:</span>
                <span className="font-semibold">R{token.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="opacity-70">Source:</span>
                <span className="font-semibold capitalize">{token.token_source}</span>
              </div>
              {token.ace_amount && (
                <div className="flex justify-between">
                  <span className="opacity-70">ACE Tokens:</span>
                  <span className="font-semibold">{token.ace_amount.toFixed(4)} ACE</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="opacity-70">Expires in:</span>
                <span className="font-semibold">{Math.floor(token.expires_in / 60)} mins</span>
              </div>
            </div>
            <p className="text-xs mt-3 opacity-80 bg-white/10 p-2 rounded">
              Use this token for payments within 15 minutes
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Send Money Dialog
const SendMoneyDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({ recipient_phone: '', amount: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/money-transfer`,
        { 
          recipient_phone: formData.recipient_phone, 
          amount: parseFloat(formData.amount),
          transfer_type: 'send'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success('Money sent successfully');
      setFormData({ recipient_phone: '', amount: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send money');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="send-money-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Send Money</DialogTitle>
          <DialogDescription>Transfer money using ACE stable coin</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <Label htmlFor="send_phone">Recipient Phone Number</Label>
            <Input
              id="send_phone"
              data-testid="send-phone-input"
              placeholder="+27XXXXXXXXX"
              value={formData.recipient_phone}
              onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="send_amount">Amount (ZAR)</Label>
            <Input
              id="send_amount"
              type="number"
              step="0.01"
              data-testid="send-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="submit-send-money-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Money'}
          </Button>
        </form>

        {result && result.transfer_code && (
          <div className="mt-4 p-4 bg-[#145539] text-white rounded-lg" data-testid="send-money-result">
            <p className="text-sm opacity-70">Transfer Code</p>
            <p className="text-2xl font-bold">{result.transfer_code}</p>
            <p className="text-xs mt-2">Share this code with the recipient</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Buy ACE Dialog
const BuyACEDialog = ({ open, onClose, wallet }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [aceInfo, setAceInfo] = useState(null);
  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) {
      fetchACEInfo();
    }
  }, [open]);

  const fetchACEInfo = async () => {
    try {
      const response = await axios.get(`${API}/ace-token/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAceInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch ACE info');
    }
  };

  const calculateTokens = () => {
    if (!amount || !aceInfo) return '0.0000';
    return (parseFloat(amount) / aceInfo.current_value_zar).toFixed(4);
  };

  const handleBuy = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ace-token`,
        { amount: parseFloat(amount), operation: 'buy' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Purchased ${response.data.ace_tokens} ACE tokens @ R${response.data.token_value}`);
      setAmount('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to buy ACE tokens');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="buy-ace-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Buy ACE Token</DialogTitle>
          <DialogDescription>Carbon tax-backed token on Hedera DLT</DialogDescription>
        </DialogHeader>
        
        {aceInfo && (
          <div className="p-4 bg-gradient-to-br from-[#145539] to-[#1e7049] text-white rounded-lg mb-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-80">Current Token Value</p>
                <p className="text-2xl font-bold">R {aceInfo.current_value_zar}</p>
              </div>
              <Coins className="w-12 h-12 opacity-20" />
            </div>
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs opacity-80">💹 Based on SA Carbon Tax (R{aceInfo.carbon_tax_rate}/tonne - 25%)</p>
              <p className="text-xs opacity-80 mt-1">🔄 {aceInfo.days_remaining_in_cycle} days left in {aceInfo.minting_cycle_years}yr cycle</p>
            </div>
          </div>
        )}

        <div className="p-4 bg-[#FAF5F0] rounded-lg">
          <p className="text-sm text-[#1B0C34]/60">Your Wallet Balance</p>
          <p className="text-2xl font-bold text-[#1B0C34]">R {wallet?.balance?.toFixed(2) || '0.00'}</p>
        </div>
        
        <form onSubmit={handleBuy} className="space-y-4">
          <div>
            <Label htmlFor="ace_buy_amount">Amount to Spend (ZAR)</Label>
            <Input
              id="ace_buy_amount"
              type="number"
              step="0.01"
              data-testid="ace-buy-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            {aceInfo && amount && (
              <p className="text-sm text-[#145539] font-medium mt-2">
                ≈ {calculateTokens()} ACE tokens
              </p>
            )}
            {aceInfo && (
              <p className="text-xs text-[#1B0C34]/60 mt-1">
                1 ACE = R{aceInfo.current_value_zar} • Value appreciates with carbon tax
              </p>
            )}
          </div>
          <Button
            type="submit"
            data-testid="submit-buy-ace-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            disabled={loading}
          >
            {loading ? 'Processing on Hedera DLT...' : 'Buy ACE Tokens'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Airtime & Data Dialog
const AirtimeDataDialog = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    phone_number: '',
    product_type: 'airtime',
    amount: '',
    provider: 'MTN'
  });
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('mojo_token');

  const handlePurchase = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/airtime-data`,
        {
          phone_number: formData.phone_number,
          product_type: formData.product_type,
          amount: parseFloat(formData.amount),
          provider: formData.provider
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Purchase successful! Voucher: ${response.data.voucher_code}`);
      setFormData({ ...formData, phone_number: '', amount: '' });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="airtime-data-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34]">Buy Airtime & Data</DialogTitle>
          <DialogDescription>Purchase airtime or data bundles</DialogDescription>
        </DialogHeader>
        <form onSubmit={handlePurchase} className="space-y-4">
          <div>
            <Label>Product Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid="customer-select-airtime-btn"
                variant={formData.product_type === 'airtime' ? 'default' : 'outline'}
                className={formData.product_type === 'airtime' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, product_type: 'airtime' })}
              >
                Airtime
              </Button>
              <Button
                type="button"
                data-testid="customer-select-data-btn"
                variant={formData.product_type === 'data' ? 'default' : 'outline'}
                className={formData.product_type === 'data' ? 'bg-[#F2B10C] text-[#1B0C34] flex-1' : 'flex-1'}
                onClick={() => setFormData({ ...formData, product_type: 'data' })}
              >
                Data
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="customer_provider">Provider</Label>
            <Select value={formData.provider} onValueChange={(value) => setFormData({ ...formData, provider: value })}>
              <SelectTrigger data-testid="customer-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MTN">MTN</SelectItem>
                <SelectItem value="Vodacom">Vodacom</SelectItem>
                <SelectItem value="Cell C">Cell C</SelectItem>
                <SelectItem value="Telkom">Telkom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="customer_phone">Phone Number</Label>
            <Input
              id="customer_phone"
              data-testid="customer-airtime-phone-input"
              placeholder="+27XXXXXXXXX"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              required
            />
          </div>
          <div>
            <Label htmlFor="customer_ad_amount">Amount (ZAR)</Label>
            <Input
              id="customer_ad_amount"
              type="number"
              step="0.01"
              data-testid="customer-airtime-amount-input"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="customer-submit-airtime-btn"
            className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            disabled={loading}
          >
            {loading ? 'Processing...' : `Purchase ${formData.product_type}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Profile Dialog Component
const ProfileDialog = ({ open, onClose, user, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    id_number: user?.id_number || '',
  });

  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open) {
      setFormData({
        full_name: user?.full_name || '',
        email: user?.email || '',
        phone_number: user?.phone_number || '',
        id_number: user?.id_number || '',
      });
      setEditing(false);
    }
  }, [open, user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/users/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Profile updated successfully!');
      setEditing(false);
      
      // Update localStorage
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('mojo_user', JSON.stringify(updatedUser));
      
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <UserCheck className="w-6 h-6" />
            Profile & Settings
          </DialogTitle>
          <DialogDescription>Manage your account details and contact information</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Edit/Save Buttons */}
          <div className="flex justify-end gap-2">
            {!editing ? (
              <Button onClick={() => setEditing(true)} className="bg-[#F2B10C] text-[#1B0C34] hover:bg-[#d99a0a]">
                Edit Profile
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => {
                  setEditing(false);
                  setFormData({
                    full_name: user?.full_name || '',
                    email: user?.email || '',
                    phone_number: user?.phone_number || '',
                    id_number: user?.id_number || '',
                  });
                }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#145539] hover:bg-[#0d3b26]">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>

          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
              <div>
                <Label>ID Number</Label>
                <Input
                  value={formData.id_number}
                  disabled={true}
                  className="bg-gray-100"
                  title="ID number cannot be changed"
                />
                <p className="text-xs text-gray-500 mt-1">ID number cannot be changed</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  disabled={!editing}
                  className={!editing ? 'bg-gray-50' : ''}
                  placeholder="+27821234567"
                />
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div>
            <h3 className="text-lg font-semibold text-[#1B0C34] mb-4">Verification Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                {user?.biometric_verified ? (
                  <><CheckCircle className="w-5 h-5 text-green-600" /> <span className="text-green-600 font-medium">ID Verified</span></>
                ) : (
                  <><AlertCircle className="w-5 h-5 text-yellow-600" /> <span className="text-yellow-600 font-medium">ID Not Verified</span></>
                )}
              </div>
              {user?.verification_score && (
                <div>
                  <span className="text-sm text-gray-600">Verification Score: </span>
                  <span className="font-semibold text-[#145539]">{user.verification_score}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Transactions Dialog Component
const TransactionsDialog = ({ open, onClose, wallet }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, sent, received

  const token = localStorage.getItem('mojo_token');

  useEffect(() => {
    if (open && wallet?.wallet_id) {
      fetchTransactions();
    }
  }, [open, wallet?.wallet_id]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    if (filter === 'sent') return tx.transaction_type?.includes('send') || tx.transaction_type?.includes('payment');
    if (filter === 'received') return tx.transaction_type?.includes('receive') || tx.transaction_type?.includes('deposit');
    return true;
  });

  const getTransactionIcon = (type) => {
    if (type?.includes('send') || type?.includes('payment')) return <ArrowUpRight className="w-5 h-5 text-red-600" />;
    if (type?.includes('receive') || type?.includes('deposit')) return <ArrowUpRight className="w-5 h-5 text-green-600 rotate-180" />;
    return <Receipt className="w-5 h-5 text-gray-600" />;
  };

  const getTransactionColor = (type) => {
    if (type?.includes('send') || type?.includes('payment')) return 'text-red-600';
    if (type?.includes('receive') || type?.includes('deposit')) return 'text-green-600';
    return 'text-gray-600';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#1B0C34] flex items-center gap-2">
            <Receipt className="w-6 h-6" />
            Recent Transactions
          </DialogTitle>
          <DialogDescription>View your transaction history and activity</DialogDescription>
        </DialogHeader>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b pb-3">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-[#1B0C34]' : ''}
          >
            All
          </Button>
          <Button
            variant={filter === 'sent' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('sent')}
            className={filter === 'sent' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            Sent
          </Button>
          <Button
            variant={filter === 'received' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('received')}
            className={filter === 'received' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            Received
          </Button>
        </div>

        {/* Transactions List */}
        <div className="flex-1 overflow-y-auto space-y-2 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B0C34] mx-auto mb-4"></div>
                <p className="text-gray-500">Loading transactions...</p>
              </div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No transactions found</p>
                <p className="text-sm text-gray-400 mt-1">
                  {filter === 'all' ? 'You have no transactions yet' : `No ${filter} transactions`}
                </p>
              </div>
            </div>
          ) : (
            filteredTransactions.map((tx, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3 flex-1">
                      <div className="mt-1">
                        {getTransactionIcon(tx.transaction_type)}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-[#1B0C34] capitalize">
                          {tx.transaction_type?.replace(/_/g, ' ') || 'Transaction'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {formatDate(tx.timestamp)} at {formatTime(tx.timestamp)}
                        </p>
                        {tx.recipient_phone && (
                          <p className="text-xs text-gray-400 mt-1">To: {tx.recipient_phone}</p>
                        )}
                        {tx.description && (
                          <p className="text-xs text-gray-600 mt-1">{tx.description}</p>
                        )}
                        {tx.status && (
                          <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
                            tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                            tx.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            tx.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {tx.status}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className={`font-bold text-lg ${getTransactionColor(tx.transaction_type)}`}>
                        {tx.amount < 0 ? '-' : '+'}R {Math.abs(tx.amount).toFixed(2)}
                      </p>
                      {tx.transaction_id && (
                        <p className="text-xs text-gray-400 mt-1">ID: {tx.transaction_id.slice(0, 8)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Summary Footer */}
        {!loading && filteredTransactions.length > 0 && (
          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Transactions:</span>
              <span className="font-semibold text-[#1B0C34]">{filteredTransactions.length}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDashboard;
